import React from 'react';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { connect } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import { RenderTextField as TextField } from './RenderTextField'
import { RenderSelectField as SelectField } from './RenderSelectField';
import { validateForm } from '../utils';
import debounce from 'lodash/debounce';
import InputAdornment from '@material-ui/core/InputAdornment/InputAdornment';
import { countries } from 'country-data';
import Typography from '@material-ui/core/Typography';

const commonVoucherMsg = 'Congratulation, you have entered a valid voucher.';

const stripeCoupons = process.env.REACT_APP_API_STRIPE_COUPONS.split(',')
  .reduce((coupons, couponCode)=>{
    coupons[couponCode] = commonVoucherMsg;
    return coupons
  }, {});

const validate = values => {
  const requiredFields = [
    'shippingName',
    'email',
    'shippingAddressLineFirst',
    'shippingAddressPostalCode',
    'shippingAddressCity',
    'shippingAddressCountry',
    'prefix',
    'phone',
  ];

  return validateForm(values, requiredFields);
};

export const getSanitizedCountries = (countries) => {
  const countriesList = countries.map(({alpha2, countryCallingCodes, name}) => ({
    name,
    code: alpha2,
    prefix: countryCallingCodes[0]
  }));

  return countriesList.sort((countryA, countryB) => {
    return countryA.name.toLowerCase().localeCompare(countryB.name.toLowerCase());
  });
};

const prefixesListOrderedAlphabetically = countries.all.map(({alpha2, countryCallingCodes, name}) => ({
  name,
  code: alpha2,
  prefix: countryCallingCodes[0]
}));

const countriesList = getSanitizedCountries(countries.all);

const styles = {
  green: {
    color: '#4caf50',
  },
  red: {
    color: '#f44336',
  }
};

export const validateStripeCoupons = (formState, coupons) => {
  let shouldShow = formState && formState.coupon != null && formState.coupon !== '';

  if (shouldShow) {
    let stripeCouponInformation = getStripeCouponInformation(formState, coupons);
    let validationMessageStyle = typeof coupons[formState.coupon] !== 'undefined' ?
      styles.green : styles.red;

    return <div>
      <Typography variant="body2" style={validationMessageStyle}>
        {stripeCouponInformation}
      </Typography>
    </div>
  } else {
    return null;
  }
};

export const getStripeCouponInformation = (formState, coupons) => {
  if (formState == null || formState.coupon == null || formState.coupon === '') {
    return false;
  }

  return typeof coupons[formState.coupon] !== 'undefined' ?
    coupons[formState.coupon] : 'Please enter valid voucher code. Remember that the voucher is case sensitive.';
};

export const parsePrefix = (prefix)=>{
  if(!prefix) return '';
  const sanitizedPrefix = prefix.split("_")[1];
  return sanitizedPrefix || '';
}

class Form extends React.Component {

  componentWillUpdate(props) {
    this.onChanged(props);
  }

  onChanged = debounce((values, valid) => {
    const {onFormChange} = this.props;
    if (valid && values) {
      const {prefix, phone, ...rest} = values;
      onFormChange({shippingPhone: `${parsePrefix(prefix)}${phone}`, ...rest});
    }
  }, 600);

  render() {
    return (
      <FinalForm
        onSubmit={() => {}}
        validate={validate}
        render={({ form }) => {
          return (
          <form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name='shippingName'
                  data-testid="company-name"
                  label='Company name'
                  margin='normal'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name='email'
                  data-testid="company-email"
                  label='Invoice email'
                  margin='normal'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name='shippingAddressLineFirst'
                  data-testid="company-address-first-line"
                  label='Address line 1'
                  margin='normal'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name='shippingAddressLineSecond'
                  data-testid="company-address-second-line"
                  label='Address line 2'
                  margin='normal'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name='shippingAddressPostalCode'
                  data-testid="company-address-zip-code"
                  label='ZIP Code'
                  margin='normal'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name='shippingAddressCity'
                  data-testid="company-address-city-name"
                  label='City'
                  margin='normal'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={SelectField}
                  items={countriesList.map(({ code, name }) => ({ value: code, name }))}
                  name='shippingAddressCountry'
                  data-testid="company-address-country-name"
                  label='Country'
                  displayEmpty
                  fullWidth
                />
              </Grid>

              <Grid item xs={3}>
                <Field
                  component={SelectField}
                  items={prefixesListOrderedAlphabetically.map(({ prefix, code }) => ({
                    value: `${code}_${prefix}`,
                    name: code,
                  }))}
                  name='prefix'
                  data-testid="company-phone-prefix"
                  label='Prefix'
                  displayEmpty
                  fullWidth
                />
              </Grid>
              <Grid item xs={9}>
                <Field
                  component={TextField}
                  name='phone'
                  data-testid="company-phone-number"
                  type='phone'
                  label='Phone'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        {parsePrefix(form.getState().values.prefix)}{' '}
                      </InputAdornment>
                    ),
                  }}
                  margin='normal'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name='taxInfoTaxId'
                  label='VAT number'
                  data-testid="company-vat-number"
                  margin='normal'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name='coupon'
                  label='Voucher'
                  data-testid="voucher-code"
                  margin='normal'
                  fullWidth
                />
              </Grid>
              {validateStripeCoupons(form.getState().values, stripeCoupons)}
            </Grid>
            <FormSpy subscription={{ values: true, valid: true }}
              onChange={({ values, valid }) => this.onChanged(values, valid)} >
            </FormSpy>
          </form>
        )}}
      />
    );
  }

}

export default Form;

